import { SearchableSelect } from "@/components/ui/searchable-select";
import { CallAiActions } from "@/components/ai/CallAiActions";
import { TidyNotesButton } from "@/components/ai/TidyNotesButton";
import { RecordingPlayer } from "@/components/calling/RecordingPlayer";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, MapPin, Paperclip, Phone, Pin, StickyNote, Trash2, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { cn } from "@/lib/utils";
import { dateLocale, withClock } from "@/lib/dateTime";
import type { TimelineVisit } from "@/lib/visits";
import { VisitTimelineExtra } from "@/components/visits/VisitTimelineExtra";

type Kind = "NOTE" | "CALL" | "MEETING" | "FILE" | "VISIT";
interface Entry {
  id: string;
  activity_type: Kind;
  title: string | null;
  body: string | null;
  call_outcome: string | null;
  duration_minutes: number | null;
  occurred_at: string | null;
  file_name: string | null;
  file_size: number | null;
  has_file: boolean;
  call_id?: string | null;
  visit?: TimelineVisit | null;
  pinned: boolean;
  created_on: string;
  created_by: { id: string; name: string } | null;
  can_change: boolean;
}
interface FileRow {
  id: string;
  source: string;
  name: string;
  title: string | null;
  size: number | null;
  uploaded_on: string;
  uploaded_by: string | null;
  deal: { id: string; name: string } | null;
  download: string;
}

const OUTCOME: Record<string, string> = { CONNECTED: "connected", NO_ANSWER: "no answer", BUSY: "busy", VOICEMAIL: "voicemail", WRONG_NUMBER: "wrong number", CALL_BACK: "call back" };
const ICON = { NOTE: StickyNote, CALL: Phone, MEETING: Video, FILE: Paperclip, VISIT: MapPin } as const;
const TONE = { NOTE: "bg-warning-soft text-warning", CALL: "bg-accent text-accent-foreground", MEETING: "bg-info-soft text-info", FILE: "bg-muted text-muted-foreground", VISIT: "bg-success-soft text-success" } as const;
const clock = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
const dayLabel = (iso: string) => {
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  const d = new Date(iso);
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "long", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};
const size = (n: number | null) => (n == null ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * The log of an account, contact or lead: notes, calls, meetings and files, with a box to add each.
 * An account's log also holds its contacts' entries; the Files filter lists every file, including the deals'.
 */
export function ActivityLog({ relatedType, relatedId, canCreate }: { relatedType: "ACCOUNT" | "CONTACT" | "LEAD"; relatedId: string; canCreate: boolean }) {
  const { success, error } = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<Entry[]>([]);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [filter, setFilter] = useState<"ALL" | Kind>("ALL");
  const [more, setMore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [dialog, setDialog] = useState<"CALL" | "MEETING" | null>(null);
  const [form, setForm] = useState({ outcome: "CONNECTED", minutes: "", title: "", when: "", text: "" });
  const fileInput = useRef<HTMLInputElement>(null);

  const base = `related_type=${relatedType}&related_id=${relatedId}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (filter === "FILE") {
        setFiles(unwrapArray<FileRow>(await api.get(`/api/CustomerActivity/Files?${base}`)));
        setRows([]);
        setMore(null);
      } else {
        const r = unwrapObject<{ rows: Entry[]; has_more: boolean; next_before: string | null }>(await api.get(`/api/CustomerActivity/List?${base}${filter === "ALL" ? "" : `&activity_type=${filter}`}`));
        setRows(r.rows);
        setMore(r.has_more ? r.next_before : null);
      }
    } catch (e: any) {
      error("Could not load the log", e?.message || "Try again");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, filter]);

  useEffect(() => {
    load();
  }, [load]);

  // A call just saved from the softphone: show it without a page reload
  useEffect(() => {
    const onSaved = () => void load();
    window.addEventListener("crm:call-saved", onSaved);
    return () => window.removeEventListener("crm:call-saved", onSaved);
  }, [load]);

  const loadMore = async () => {
    if (!more) return;
    const r = unwrapObject<{ rows: Entry[]; has_more: boolean; next_before: string | null }>(await api.get(`/api/CustomerActivity/List?${base}${filter === "ALL" ? "" : `&activity_type=${filter}`}&before=${encodeURIComponent(more)}`));
    setRows((cur) => [...cur, ...r.rows]);
    setMore(r.has_more ? r.next_before : null);
  };

  const insert = async (payload: Record<string, any>, done: string) => {
    setBusy(true);
    try {
      await api.post("/api/CustomerActivity/Insert", { related_type: relatedType, related_id: relatedId, ...payload });
      success(done, "Added to the log");
      await load();
      return true;
    } catch (e: any) {
      error("Could not save", e?.message || "Try again");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    if (await insert({ activity_type: "NOTE", body: note.trim() }, "Note saved")) setNote("");
  };

  const saveDialog = async () => {
    const ok =
      dialog === "CALL"
        ? await insert({ activity_type: "CALL", call_direction: "OUTBOUND", call_outcome: form.outcome, duration_minutes: form.minutes ? Number(form.minutes) : undefined, body: form.text.trim() || undefined, occurred_at: new Date().toISOString() }, "Call logged")
        : await insert({ activity_type: "MEETING", title: form.title.trim() || "Meeting", body: form.text.trim() || undefined, occurred_at: form.when ? new Date(form.when).toISOString() : new Date().toISOString() }, "Meeting logged");
    if (ok) {
      setDialog(null);
      setForm({ outcome: "CONNECTED", minutes: "", title: "", when: "", text: "" });
    }
  };

  const upload = async (file?: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("related_type", relatedType);
      fd.append("related_id", relatedId);
      await api.post("/api/CustomerActivity/Upload", fd);
      success("File kept", file.name);
      await load();
    } catch (e: any) {
      error("Could not upload", e?.message || "Try again");
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const pin = async (e: Entry) => {
    try {
      await api.put(`/api/CustomerActivity/Update/${e.id}`, { pinned: !e.pinned });
      await load();
    } catch (err: any) {
      error("Could not pin", err?.message || "Try again");
    }
  };

  const remove = async (e: Entry) => {
    if (!(await confirm({ title: "Remove this entry?", message: e.has_file ? "Its file is removed too." : "It is removed from the log.", confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.delete(`/api/CustomerActivity/Delete/${e.id}`);
      await load();
    } catch (err: any) {
      error("Could not remove", err?.message || "Try again");
    }
  };

  const days = useMemo(() => {
    const out: { label: string; items: Entry[] }[] = [];
    for (const r of rows) {
      const label = r.pinned ? "Pinned" : dayLabel(r.created_on);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(r);
      else out.push({ label, items: [r] });
    }
    return out;
  }, [rows]);

  const chips: { key: "ALL" | Kind; label: string }[] = [
    { key: "ALL", label: "All" },
    { key: "NOTE", label: "Notes" },
    { key: "CALL", label: "Calls" },
    { key: "MEETING", label: "Meetings" },
    { key: "VISIT", label: "Visits" },
    { key: "FILE", label: "Files" },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {canCreate && (
        <div className="flex flex-col rounded-lg border border-border bg-card">
          <div role="group" aria-label="Add to the log" className="flex flex-wrap gap-1 border-b border-border/70 px-2.5 py-2">
            <span className="flex h-[26px] items-center gap-1.5 rounded-md bg-sidebar px-2.5 text-xs font-semibold text-white">
              <StickyNote className="size-3.5" /> Note
            </span>
            <button type="button" onClick={() => setDialog("CALL")} className="flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
              <Phone className="size-3.5" /> Call
            </button>
            <button type="button" onClick={() => setDialog("MEETING")} className="flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
              <Video className="size-3.5" /> Meeting
            </button>
            <button type="button" onClick={() => fileInput.current?.click()} className="flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
              <Paperclip className="size-3.5" /> File
            </button>
            <input ref={fileInput} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </div>
          <label className="px-3.5 pt-2.5">
            <span className="sr-only">Note</span>
            <MentionTextarea rows={2} value={note} onChange={setNote} placeholder="Write a note… type @ to mention someone" className="w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-muted-foreground" />
          </label>
          <div className="flex justify-end px-2.5 pb-2.5 pt-1.5">
            <Button size="sm" disabled={busy || !note.trim()} onClick={saveNote}>
              Save note
            </Button>
          </div>
        </div>
      )}

      <div role="tablist" aria-label="Filter the log" className="flex flex-wrap gap-1">
        {chips.map((c) => (
          <button key={c.key} type="button" role="tab" aria-selected={filter === c.key} onClick={() => setFilter(c.key)} className={cn("h-7 rounded-md px-2.5 text-xs", filter === c.key ? "bg-card font-semibold shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted")}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
      ) : filter === "FILE" ? (
        files.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border py-8 text-center text-xs text-muted-foreground">No files yet.</p>
        ) : (
          <div className="rounded-lg border border-border bg-card">
            {files.map((f, i) => (
              <a key={`${f.source}-${f.id}`} href={f.download} target="_blank" rel="noreferrer" className={cn("flex items-center gap-3 px-3.5 py-2.5 hover:bg-muted/50", i && "border-t border-border/60")}>
                <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <FileText className="size-3.5" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px] font-medium">{f.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {[f.deal ? `Deal: ${f.deal.name}` : f.source === "CUSTOMER" ? "On the customer" : "Deal file", f.uploaded_by, size(f.size), new Date(f.uploaded_on).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </a>
            ))}
          </div>
        )
      ) : days.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border py-8 text-center text-xs text-muted-foreground">Nothing logged yet.</p>
      ) : (
        days.map((d) => (
          <div key={d.label} className="flex flex-col gap-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{d.label}</h3>
            <div className="rounded-lg border border-border bg-card">
              {d.items.map((e, i) => {
                const Icon = ICON[e.activity_type] || StickyNote;
                const title =
                  e.activity_type === "NOTE" ? "Note" : e.activity_type === "CALL" ? `Call · ${OUTCOME[e.call_outcome || ""] || "logged"}${e.duration_minutes ? ` · ${e.duration_minutes} min` : ""}` : e.activity_type === "FILE" ? e.file_name || e.title || "File" : e.activity_type === "VISIT" ? `${e.title || "Visit"}${e.duration_minutes != null ? ` · ${e.duration_minutes} min` : ""}` : e.title || "Meeting";
                return (
                  <div key={e.id} className={cn("group flex gap-3 px-3.5 py-2.5", i && "border-t border-border/60")}>
                    <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-lg", TONE[e.activity_type])}>
                      <Icon className="size-3.5" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex justify-between gap-2.5">
                        <span className="text-[13px] font-medium">
                          {e.has_file ? (
                            <a href={`/api/CustomerActivity/File/${e.id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                              {title}
                            </a>
                          ) : (
                            title
                          )}
                        </span>
                        <span className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[11.5px] text-muted-foreground">
                          {e.can_change && (
                            <>
                              <button type="button" aria-label={e.pinned ? "Unpin" : "Pin to the top"} title={e.pinned ? "Unpin" : "Pin to the top"} onClick={() => pin(e)} className={cn("hover:text-primary", e.pinned ? "text-primary" : "hidden group-hover:inline")}>
                                <Pin className={cn("size-3.5", e.pinned && "fill-current")} />
                              </button>
                              <button type="button" aria-label="Remove" title="Remove" onClick={() => remove(e)} className="hidden hover:text-danger group-hover:inline">
                                <Trash2 className="size-3.5" />
                              </button>
                            </>
                          )}
                          {clock(e.created_on)}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">{[e.created_by?.name, e.activity_type === "FILE" ? size(e.file_size) : ""].filter(Boolean).join(" · ")}</span>
                      {e.body && <p className="mt-1 whitespace-pre-wrap rounded-md bg-panel px-2.5 py-2 text-[13px]">{e.body}</p>}
                      <RecordingPlayer callId={e.call_id} />
                      {e.call_id && (
                        <div className="mt-1">
                          <CallAiActions callId={e.call_id} variant="link" />
                        </div>
                      )}
                      <VisitTimelineExtra visit={e.visit} label={e.title || undefined} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
      {more && filter !== "FILE" && (
        <Button variant="outline" size="sm" onClick={loadMore} className="self-center">
          Show older
        </Button>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog === "CALL" ? "Log a call" : "Log a meeting"}</DialogTitle>
            <DialogDescription>{dialog === "CALL" ? "How the call went. It is added to the log." : "What was discussed. It is added to the log."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dialog === "CALL" ? (
              <div className="flex gap-2">
                <SearchableSelect value={form.outcome} onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))} className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm">
                  {Object.entries(OUTCOME).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </option>
                  ))}
                </SearchableSelect>
                <Input type="number" min={0} max={600} placeholder="Minutes" className="w-28" value={form.minutes} onChange={(e) => setForm((f) => ({ ...f, minutes: e.target.value }))} />
              </div>
            ) : (
              <>
                <Input placeholder="Meeting with…, about…" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                <Input type="datetime-local" value={form.when} onChange={(e) => setForm((f) => ({ ...f, when: e.target.value }))} />
              </>
            )}
            <textarea rows={3} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="Notes" className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none" />
            <TidyNotesButton text={form.text} kind={dialog === "CALL" ? "CALL" : "MEETING"} onTidied={(n) => setForm((f) => ({ ...f, text: n }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={saveDialog}>
              {dialog === "CALL" ? "Log call" : "Log meeting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
